/**
 * GPS Statistics Dashboard Component
 * Shows key metrics about GPS devices and fleet activity
 */

import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { useTheme, Text, Surface } from 'react-native-paper';
import gpsTrackingAPI from '../../api/services/gpsTracking';
import type { LivePosition, GPSDevice } from '../../types/gps';

interface GPSStatisticsProps {
  devices: LivePosition[];
  onRefresh?: () => void;
}

interface Statistics {
  totalDevices: number;
  onlineDevices: number;
  offlineDevices: number;
  devicesWithVehicles: number;
  totalDistanceToday: number;
  unacknowledgedAlerts: number;
}

export default function GPSStatistics({ devices, onRefresh }: GPSStatisticsProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [unacknowledgedAlerts, setUnacknowledgedAlerts] = useState(0);

  useEffect(() => {
    loadUnacknowledgedAlerts();
  }, []);

  const loadUnacknowledgedAlerts = async () => {
    try {
      const alerts = await gpsTrackingAPI.alerts.getUnacknowledged();
      setUnacknowledgedAlerts(alerts.length);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  };

  // Calculate statistics
  const stats: Statistics = {
    totalDevices: devices.length,
    onlineDevices: devices.filter(d => d.is_online).length,
    offlineDevices: devices.filter(d => !d.is_online).length,
    devicesWithVehicles: devices.filter(d => d.vehicle !== null).length,
    totalDistanceToday: 0, // Would need trip data from today
    unacknowledgedAlerts,
  };

  const StatCard = ({
    icon,
    label,
    value,
    suffix = '',
    color = '#007AFF',
    onPress,
  }: {
    icon: string;
    label: string;
    value: number | string;
    suffix?: string;
    color?: string;
    onPress?: () => void;
  }) => {
    const CardWrapper = onPress ? TouchableOpacity : View;

    return (
      <CardWrapper
        style={[styles.statCard, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.outlineVariant }, onPress && { borderColor: theme.colors.primary, backgroundColor: theme.colors.primaryContainer }]}
        onPress={onPress}
      >
        <View style={[styles.statIcon, { backgroundColor: `${color}20` }]}>
          <Ionicons name={icon as any} size={18} color={color} />
        </View>
        <Text variant="titleMedium" style={[styles.statValue, { color: theme.colors.onSurface }]}>
          {value}
          {suffix && <Text variant="bodySmall" style={[styles.statSuffix, { color: theme.colors.onSurfaceVariant }]}>{suffix}</Text>}
        </Text>
        <Text variant="labelSmall" style={[styles.statLabel, { color: theme.colors.onSurfaceVariant }]}>{label}</Text>
      </CardWrapper>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.surface, borderBottomColor: theme.colors.outlineVariant }]}>
      <View style={styles.header}>
        <Text variant="labelLarge" style={[styles.title, { color: theme.colors.onSurface }]}>Estatísticas</Text>
        {onRefresh && (
          <TouchableOpacity onPress={onRefresh} disabled={loading}>
            {loading ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <Ionicons name="refresh" size={20} color={theme.colors.primary} />
            )}
          </TouchableOpacity>
        )}
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.statsScroll}
        contentContainerStyle={styles.statsContent}
      >
        <StatCard
          icon="car-sport"
          label={t('gps.devices')}
          value={stats.totalDevices}
          color={theme.colors.primary}
        />
        
        <StatCard
          icon="checkmark-circle"
          label={t('gps.online')}
          value={stats.onlineDevices}
          color={theme.colors.primary}
        />
        
        <StatCard
          icon="close-circle"
          label={t('gps.offline')}
          value={stats.offlineDevices}
          color={theme.colors.onSurfaceVariant}
        />
        
        <StatCard
          icon="link"
          label="Com Veículo"
          value={stats.devicesWithVehicles}
          color={theme.dark ? theme.colors.onTertiaryContainer : theme.colors.tertiary}
        />
        
        {stats.unacknowledgedAlerts > 0 && (
          <StatCard
            icon="notifications"
            label="Alertas Pendentes"
            value={stats.unacknowledgedAlerts}
            color={theme.colors.error}
            onPress={() => {
              // Navigate to alerts page
              router.push('/dashboard/gps/alerts');
            }}
          />
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 8,
    borderBottomWidth: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    marginBottom: 6,
  },
  title: {
    fontWeight: '600',
  },
  statsScroll: {
    paddingHorizontal: 16,
  },
  statsContent: {
    gap: 8,
  },
  statCard: {
    padding: 10,
    borderRadius: 8,
    minWidth: 90,
    alignItems: 'center',
    borderWidth: 1,
  },
  statCardTouchable: {
  },
  statIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  statValue: {
    fontWeight: '700',
    marginBottom: 2,
  },
  statSuffix: {
    fontWeight: '500',
  },
  statLabel: {
    textAlign: 'center',
  },
});
